// src/app/services/environment-config-service/environment-config.service.ts
import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Browser } from '@capacitor/browser';
import { App, AppInfo } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';

import { firstValueFrom, timeout } from 'rxjs';

import {
  CountryItemModel,
  GetVivaScoreRequestModel,
  IAppEnvironment,
  SupportRequestModel,
  VivaPingRequest,
} from 'src/app/api/proxy/app-environment/app-environment-models';
import {
  appEnvironmentVivaScorePingPost,
  appEnvironmentMyCountryPost,
  appEnvironmentCountriesGet,
  appEnvironmentCountriesForUserDeviceTypeAppIdProductionGet,
  appEnvironmentGetVivaScoreAsyncPost,
  appEnvironmentSupportRequestPost,
  appEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGet
} from 'src/app/api/proxy/app-environment/functions';
import { AppEnvironmentApiConfiguration } from 'src/app/api/proxy/app-environment/app-environment-api-configuration';

import { AuthenticationApiConfiguration } from 'src/app/api/proxy/auth/authentication-api-configuration';
import { FaceScanApiConfiguration } from 'src/app/api/proxy/facescan/face-scan-api-configuration';
import { OrganizationApiConfiguration } from 'src/app/api/proxy/organization/organization-api-configuration';
import { ReportApiConfiguration } from 'src/app/api/proxy/report/report-api-configuration';
import { VitalScoreApiConfiguration } from 'src/app/api/proxy/vitalscore/vital-score-api-configuration';
import { VirtualHealthLibraryApiConfiguration } from 'src/app/api/proxy/virtualHealthLibrary/virtual-health-library-api-configuration';
import { WearablesApiConfiguration } from 'src/app/api/proxy/wearables/wearables-api-configuration';
import { NotifyApiConfiguration } from 'src/app/api/proxy/notify/notify-api-configuration';
import { EmotionApiConfiguration } from 'src/app/api/proxy/emotion/emotion-api-configuration';
import { DataPointsApiConfiguration } from 'src/app/api/proxy/datapoints/data-points-api-configuration';
import { PointsApiConfiguration } from 'src/app/api/proxy/points/points-api-configuration';
import { QuestionnaireApiConfiguration } from 'src/app/api/proxy/questionnaire/questionnaire-api-configuration';
import { MPIApiConfiguration } from 'src/app/api/proxy/mpi/mpi-api-configuration';
import { VivaLiteScanApiConfiguration } from 'src/app/api/proxy/vivalite-scan/viva-lite-scan-api-configuration';
import { hatsAppApiConfiguration } from 'src/app/api/proxy/whatsapp/hats-app-api-configuration';

import { EventService } from '../events/event.service';
import { PromptService } from '../promtp-service/prompt.service';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { StorageKey } from '../storage/models/storage-key.enum';
import { SystemLogService } from '../systemlog-service/systemlog.service';
import { LoadingService } from '../loading-service/loading.service';

import { sharedConfig } from 'src/environments/shared-config';
import { environment } from 'src/environments/environment';
import { UserData } from '../auth-service/user-data.model';
import { UserInfoService } from '../auth-service/user-info-service';
import { TranslatorService } from '../translator-service/translator.service';
import { shouldWarnAboutRegionChange, resolveCountryCodeForBuild } from './region-change.util';

@Injectable({ providedIn: 'root' })
export class EnvironmentConfigService {
  // ─────────────── constants ───────────────
  private readonly PING_TIMEOUT_MS = 5000;
  private readonly NETWORK_TIMEOUT_MS = 5000;
  private readonly ENV_TTL_MS = 36 * 60 * 60 * 1000; // 36 hours
  private readonly LOG = false;
  private readonly TAG = 'ENV';

  // ─────────────── state ───────────────
  private _env: ILocalAppEnvironment | null = null;
  private _countries?: CountryItemModel[];
  private _selectedRegion: CountryItemModel | null | undefined = null;
  private _myCountry: CountryItemModel | null = null;

  // single-flight guards
  private initPromise: Promise<void> | null = null;
  private loadPromise: Promise<void> | null = null;
  private pingPromise: Promise<DevicePingResponse> | null = null;

  // idempotent “applied” guard
  private lastAppliedEnvJson: string | null = null;

  constructor(
    private http: HttpClient,
    private appEnvCfg: AppEnvironmentApiConfiguration,
    private vitalCfg: VitalScoreApiConfiguration,
    private faceCfg: FaceScanApiConfiguration,
    private wearCfg: WearablesApiConfiguration,
    private authCfg: AuthenticationApiConfiguration,
    private vhlCfg: VirtualHealthLibraryApiConfiguration,
    private orgCfg: OrganizationApiConfiguration,
    private reportCfg: ReportApiConfiguration,
    private notifyCfg: NotifyApiConfiguration,
    private emotionCfg: EmotionApiConfiguration,
    private datapointsCfg: DataPointsApiConfiguration,
    private pointsCfg: PointsApiConfiguration,
    private questionnaireCfg: QuestionnaireApiConfiguration,
    private mpiCfg: MPIApiConfiguration,
    private vivaLiteScanCfg: VivaLiteScanApiConfiguration,
    private whatsAppCfg: hatsAppApiConfiguration,
    private prompt: PromptService,
    private injector: Injector,
  ) {
    this.vivaLiteScanCfg.rootUrl = sharedConfig.vivaLiteHealthCheckBase;
    this.appEnvCfg.rootUrl = environment.appEnvironmentBaseUrl;
  }

  // ─────────────────────────────── public API ───────────────────────────────

  public async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = Promise.resolve();
    return this.initPromise;
  }

  public UserCanLogin(): boolean {
    return this._env != null;
  }

  public IsProduction(): boolean {
    return environment.apiProduction ?? environment.production;
  }

  public InstallSource(): string | null | undefined {
    return this._env?.installSource ?? null;
  }

  /** Whether the build targets UAT API backends (inverse of IsProduction). */
  private defaultIsUATToggle(): boolean {
    return !this.IsProduction();
  }

  /** Check if running in E2E test mode */
  private isE2ETestMode(): boolean {
    // Check environment flag (set in environment.ts or via Angular build configuration)
    // E2E_TEST_MODE is now defined in all environment files (defaults to false)
    return environment.E2E_TEST_MODE === true;
  }

  /** Get mock environment configuration for E2E tests */
  private getMockEnvironmentConfig(): ILocalAppEnvironment {
    // Return mock config - this is a simple implementation
    // In a real scenario, you might want to load from a JSON file or use a more sophisticated approach
    // For now, return a minimal valid config structure
    return {
      build: '1.0.0-e2e',
      deviceType: 'web',
      id: 'mock-app-id',
      name: 'VitalScore E2E Test',
      version: '1.0.0',
      installSource: 'e2e-test',
      production: false,
      authenticateOptions: {
        appId: 'mock-app-id',
        scope: 'mock-scope',
        authorizationServiceURL: 'http://localhost:4200/mock/auth',
      },
      vitalScoreConfig: {
        vitalScoreBase: 'http://localhost:4200/mock/vitalscore',
      },
      faceScanConfig: {
        faceScanBase: 'http://localhost:4200/mock/facescan',
      },
      wearableConfig: {
        wearableV3Base: 'http://localhost:4200/mock/wearables',
      },
      notifyConfig: {
        notifyBase: 'http://localhost:4200/mock/notify',
      },
      virtualHealthLibraryConfig: {
        virtualHealthLibraryBase: 'http://localhost:4200/mock/vhl',
      },
      organizationConfig: {
        organizationBase: 'http://localhost:4200/mock/organization',
      },
      mpiConfig: {
        mpiBase: 'http://localhost:4200/mock/mpi',
      },
      pointsConfig: {
        pointsBase: 'http://localhost:4200/mock/points',
      },
      reportingConfig: {
        reportingBase: 'http://localhost:4200/mock/report',
      },
      emotionConfig: {
        emotionBase: 'http://localhost:4200/mock/emotion',
      },
      dataPointsConfig: {
        dataPointsBase: 'http://localhost:4200/mock/datapoints',
      },
      questionnaireConfig: {
        questionnaireBase: 'http://localhost:4200/mock/questionnaire',
      },
      whatsAppConfig: {
        whatsAppBase: 'http://localhost:4200/mock/whatsapp',
        whatsAppNumber: null,
      },
    } as ILocalAppEnvironment;
  }

  /** Ensure a usable environment is loaded & applied. Idempotent & single-flight. */
  public async EnsureNetworkConfiguration(source: string): Promise<ILocalAppEnvironment | null> {
    this.L('EnsureNetworkConfiguration', source);

    // If in E2E test mode, return mock configuration immediately
    if (this.isE2ETestMode()) {
      this.L('EnsureNetworkConfiguration', 'E2E test mode - returning mock config');
      const mockConfig = this.getMockEnvironmentConfig();
      this._env = mockConfig;
      this.applyEnvIfChanged();
      return mockConfig;
    }
    await this.init();
    await this.ensureLoaded(`EnsureNetworkConfiguration:${source}`, /*forced*/ false);
    return this._env;
  }

  /** Kept for compatibility; same behavior as EnsureNetworkConfiguration. */
  public async EnsureNetworkConfigurationForToken(): Promise<ILocalAppEnvironment | null> {
    await this.init();
    await this.ensureLoaded('EnsureNetworkConfigurationForToken', /*forced*/ false);
    return this._env;
  }

  public async initService(): Promise<void> {
    await this.init();
    await this.ensureLoaded('initService', /*forced*/ false);
  }

  public getCurrentConfig(): ILocalAppEnvironment | null {
    return this._env;
  }

  /** True when environment config is loaded and all service base URLs are applied. */
  public isEnvironmentReady(): boolean {
    return !!(this._env && this.allLocationsSet());
  }

  /** WhatsApp API base URL from environment (whatsAppConfig.dataPointsBase or whatsAppConfig.whatsAppBase). */
  public getWhatsAppBaseUrl(): string | null {
    const cfg = this._env?.whatsAppConfig as { dataPointsBase?: string; whatsAppBase?: string } | undefined;
    return (cfg?.dataPointsBase ?? cfg?.whatsAppBase ?? null)?.trim() || null;
  }

  /** WhatsApp business number from environment for opt-in (whatsAppConfig.whatsAppNumber). */
  public getWhatsAppNumber(): string | null {
    return this._env?.whatsAppConfig?.whatsAppNumber ?? null;
  }

  public async OpenAppStore(): Promise<void> {
    const isIos = Capacitor.getPlatform() === 'ios';
    const url = isIos ? environment.iosStoreLink : environment.androidStoreLink;
    if (!url) return;
    await Browser.open({ url });
  }

  public async GetCountries(): Promise<CountryItemModel[] | null | undefined> {
    if (this._countries?.length) return this.filteredCountries(this._countries);

    if (!(await this.isOnline())) return this._countries;

    try {
      const obs = appEnvironmentCountriesGet(this.http, this.appEnvCfg.rootUrl);
      const list = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT_MS)));
      this._countries = list?.body ?? [];
      return this.filteredCountries(this._countries);
    } catch (err) {
      await this.prompt.showNetworkConnectionError(err, 'GetCountries');
      return this._countries;
    }
  }

  public async CurrentCountry(): Promise<CountryItemModel | null | undefined> {
    // When build has a fixed countryCode (e.g. bomaid=za), always honor it and sync storage if it differs
    const buildCountry = this.buildCountryCode;
    if (buildCountry) {
      const raw = await SecureStorageService.get(SecureStorageKey.CurrentRegion);
      let storedCode: string | null = null;
      if (raw) {
        try {
          const stored = JSON.parse(raw) as CountryItemModel;
          storedCode = stored?.countryCode?.toLowerCase() ?? null;
        } catch {
          storedCode = null;
        }
      }
      if (storedCode !== buildCountry) {
        const updated = await this.SetCountry(buildCountry);
        if (!updated) {
          // Fallback when SetCountry fails (e.g. offline): still honor build country and clear bad cache
          this._selectedRegion = { countryCode: buildCountry, countryName: buildCountry.toUpperCase() };
          await SecureStorageService.set(SecureStorageKey.CurrentRegion, JSON.stringify(this._selectedRegion));
          this._env = null;
          await SecureStorageService.remove(StorageKey.EnvironmentSettings);
          await SecureStorageService.remove(SecureStorageKey.LastEnvironmentFetchTime);
        }
      } else if (!this._selectedRegion || (this._selectedRegion.countryCode?.toLowerCase() !== buildCountry)) {
        this._selectedRegion = { countryCode: buildCountry, countryName: buildCountry.toUpperCase() };
      }
      return this._selectedRegion;
    }

    if (!this._selectedRegion) {
      const raw = await SecureStorageService.get(SecureStorageKey.CurrentRegion);
      if (raw) {
        try {
          this._selectedRegion = JSON.parse(raw);
        } catch {
          this._selectedRegion = null;
        }
      } else {
        this._selectedRegion = await this.countryFromIP();
      }
    }
    return this._selectedRegion;
  }

  public async GetCountriesForUser(email: string): Promise<CountryItemModel[] | null | undefined> {
    // Fixed country builds (e.g. bomaid): always return just the one country
    const fixed = this.buildCountryCode;
    if (fixed) {
      return [{ countryCode: fixed, countryName: fixed.toUpperCase() }];
    }

    if (!email || email === this.lastEmailTest) {
      if (this.userCountries && this.IsProduction()) {
        return this.filteredCountries(this.userCountries);
      }
    }

    try {
      const appInfo = await this.getAppInfo();
      if (!(await this.isOnline())) return this.userCountries;

      const req = {
        deviceType: Capacitor.getPlatform(),
        appId: appInfo.id!,
        production: this.IsProduction(),
        emailAddress: email,
      };

      const httpResponse = await firstValueFrom(
        appEnvironmentCountriesForUserDeviceTypeAppIdProductionGet(this.http, this.appEnvCfg.rootUrl, req).pipe(timeout(this.NETWORK_TIMEOUT_MS))
      );
      let list = httpResponse ? httpResponse.body : null;

      if (list) {
        this.lastEmailTest = email;
        list = this.filteredCountries(list) ?? list;
        if (!this.IsProduction()) list = list.filter((c) => c.countryCode === 'za'); // preserve existing behavior
        this._countries = list;
        this.userCountries = list;

        if (list.length === 1) {
          await this.SetCountry(list[0].countryCode!);
        }
      }

      return this._countries;
    } catch (err) {
      await this.prompt.showNetworkConnectionError(err, 'GetCountriesForUser');
      return null;
    }
  }

  /**
   * Sets the active country/region.
   * Fixed-region builds (environment.countryCode set) always use that code and ignore the argument.
   * @param fromRegistration When true (registration/org-code flows only), warn a logged-in user
   *   if their stored region differs and the build has no fixed environment.countryCode.
   *   Warning only — does not log out or treat the change as an error.
   */
  public async SetCountry(countryCode: string, fromRegistration: boolean = false): Promise<boolean> {
    const fixed = this.buildCountryCode;
    const code = resolveCountryCodeForBuild(countryCode, fixed);
    if (!code) {
      return false;
    }

    if (!this._countries?.length) {
      await this.GetCountries();
    }

    // Region-change warnings never apply to fixed-country builds.
    if (fromRegistration && !fixed) {
      await this.warnIfRegionChangedDuringRegistration(code);
    }

    this._selectedRegion = this._countries?.find((c) => (c.countryCode ?? '').toLowerCase() === code) ?? null;

    // Fixed builds must always persist environment.countryCode even if the countries API is empty/offline.
    if (!this._selectedRegion && fixed) {
      this._selectedRegion = { countryCode: fixed, countryName: fixed.toUpperCase() };
    }

    if (this._selectedRegion) {
      await SecureStorageService.set(SecureStorageKey.CurrentRegion, JSON.stringify(this._selectedRegion));
      // Preserve the current production/UAT setting when switching countries
      // If we have a current env, use its production flag to determine isUATToggle
      const currentProduction = this._env?.production ?? this.IsProduction();
      const isUATToggle = !currentProduction; // If production is false, we're in UAT mode
      this.L('SetCountry', 'Preserving environment setting', {
        countryCode: code,
        fixedBuildCountry: fixed ?? null,
        currentProduction: currentProduction,
        isUATToggle: isUATToggle
      });
      await this.ensureLoaded('SetCountry', /*forced*/ true, isUATToggle);
      return true;
    }
    return false;
  }

  /** Warning-only: stored CurrentRegion vs registration country for logged-in multi-region users. */
  private async warnIfRegionChangedDuringRegistration(newCountryCode: string): Promise<void> {
    const user = await UserInfoService.getUserInfo();
    const previous = await this.getStoredRegionCode();
    if (
      !shouldWarnAboutRegionChange({
        isLoggedIn: !!user?.access_token,
        fixedCountryCode: this.buildCountryCode,
        previousCountryCode: previous,
        newCountryCode,
      })
    ) {
      return;
    }

    const translator = this.injector.get(TranslatorService);
    const message = translator.translateText('environment-service.warnings.region-changed');
    await this.prompt.showWarningMessage(message);
  }

  /** Reads CurrentRegion from memory/storage without IP fallback or build-country sync. */
  private async getStoredRegionCode(): Promise<string | null> {
    if (this._selectedRegion?.countryCode) {
      return this._selectedRegion.countryCode.toLowerCase();
    }
    const raw = await SecureStorageService.get(SecureStorageKey.CurrentRegion);
    if (!raw) {
      return null;
    }
    try {
      const stored = JSON.parse(raw) as CountryItemModel;
      return stored?.countryCode?.toLowerCase() ?? null;
    } catch {
      return null;
    }
  }

  public async GetVivaScore(model: GetVivaScoreRequestModel): Promise<string | null> {
    try {
      const httpResponse = await firstValueFrom(
        appEnvironmentGetVivaScoreAsyncPost(this.http, this.appEnvCfg.rootUrl, { body: model }).pipe(timeout(this.NETWORK_TIMEOUT_MS))
      );
      return httpResponse ? httpResponse.body : null;
    } catch (err) {
      await this.prompt.showNetworkConnectionError(err, 'GetVivaScore');
      return null;
    }
  }

  /** coalesced (single-flight) ping; concurrent callers await same promise */
  public async pingService(source: string): Promise<DevicePingResponse> {
    if (this.pingPromise) return this.pingPromise;

    this.pingPromise = (async (): Promise<DevicePingResponse> => {
      const result: DevicePingResponse = {
        networkSuccess: false,
        networkTime: 0,
        deviceInfoSuccess: false,
        pingError: null,
        pingResult: null,
      };

      let personId: string | undefined | null = null;
      // Fixed country builds (e.g. bomaid): always use build country for ping region
      let region: string | undefined | null = this.buildCountryCode ?? null;

      try {
        if (region == null) {
          const rawRegion = await SecureStorageService.get(SecureStorageKey.CurrentRegion);
          if (rawRegion) {
            try {
              const r = JSON.parse(rawRegion) as CountryItemModel;
              region = r?.countryCode;
            } catch { }
          }
        }
        // person
        const rawUser = await SecureStorageService.get(SecureStorageKey.CurrentUser);
        if (rawUser) {
          try {
            const user = JSON.parse(rawUser) as UserData;
            personId = user?.tokenClaims?.personId;
          } catch { }
        }
      } catch { }

      try {
        const [id, info, langCode, langTag] = await Promise.all([
          Device.getId(),
          Device.getInfo(),
          Device.getLanguageCode(),
          Device.getLanguageTag(),
        ]);

        const payload: VivaPingRequest = {
          androidSDKVersion: info.androidSDKVersion,
          deviceTime: new Date().toISOString(),
          identifier: id.identifier,
          iosVersion: info.iOSVersion,
          isVirtual: info.isVirtual,
          languageCode: langCode.value,
          languageValue: langTag.value,
          manufacturer: info.manufacturer,
          memoryUsed: info.memUsed,
          model: info.model,
          name: info.name,
          operatingSystem: info.operatingSystem,
          osVersion: info.osVersion,
          personId,
          platform: info.platform,
          region,
          vivaAppBuild: sharedConfig.build,
          vivaAppVersion: sharedConfig.version,
          webViewVersion: info.webViewVersion,
          pingPath: source,
        };

        result.deviceInfoSuccess = true;

        if (!(await this.isOnline())) {
          result.networkSuccess = false;
          result.pingError = 'No connection';
          return result;
        }

        LoadingService.addSilentUrl(appEnvironmentVivaScorePingPost.PATH);
        try {
          const httpResponse = await firstValueFrom(
            appEnvironmentVivaScorePingPost(this.http, this.appEnvCfg.rootUrl, { body: payload }).pipe(timeout(this.PING_TIMEOUT_MS))
          );
          result.networkSuccess = true;
          result.pingResult = httpResponse ? httpResponse.body : null;
        } catch (err: unknown) {
          result.networkSuccess = false;
          result.pingError =
            err instanceof Error ? (err.name === 'TimeoutError' ? 'Timeout' : err.message) : 'Generic Error';
        }
      } catch (err) {
        result.deviceInfoSuccess = false;
        result.networkSuccess = false;
        result.pingError = 'Device info error';
      }

      return result;
    })();

    try {
      return await this.pingPromise;
    } finally {
      this.pingPromise = null;
    }
  }

  public async ContactUs(model: SupportRequestModel): Promise<string | null> {
    if (!(await this.isOnline())) return null;
    try {
      const httpResponse = await firstValueFrom(
        appEnvironmentSupportRequestPost(this.http, this.appEnvCfg.rootUrl, { body: model }).pipe(timeout(this.NETWORK_TIMEOUT_MS))
      );
      return httpResponse ? httpResponse.body : null;
    } catch (err) {
      await this.prompt.showNetworkConnectionError(err, 'ContactUs');
      return null;
    }
  }

  /** For callers expecting the old API name. */
  public async SaveEnvironmentConfig(env: ILocalAppEnvironment): Promise<void> {
    await this.saveEnvironment(env);
  }

  // ─────────────────────────────── private helpers ───────────────────────────────

  private async ensureLoaded(source: string, forced: boolean, isUATToggle?: boolean): Promise<void> {
    const effectiveUatToggle = isUATToggle ?? this.defaultIsUATToggle();
    this.L('ensureLoaded', 'BEGIN', { source, forced, isUATToggle: effectiveUatToggle });
    if (this.loadPromise) {
      this.L('ensureLoaded', 'coalesced existing');
      return this.loadPromise;
    }
    this.loadPromise = this.loadConfiguration(source, forced, effectiveUatToggle).finally(() => (this.loadPromise = null));
    return this.loadPromise;
  }

  private async loadConfiguration(source: string, forced: boolean, isUATToggle = false): Promise<void> {
    this.L('loadConfiguration', 'BEGIN', source, { forced, isUATToggle });

    // 1) Try load from storage and apply immediately if valid (but skip if forced and switching environments)
    const raw = await SecureStorageService.get(StorageKey.EnvironmentSettings);
    this.L('loadConfiguration', 'storage present=' + String(!!raw));
    if (raw) {
      try {
        this._env = JSON.parse(raw);
        this.L('loadConfiguration', 'parsed env from storage');

        // If build has a fixed countryCode (e.g. bomaid=za), reject cached env for a different country
        const envCountry = (this._env as { countryCode?: string })?.countryCode?.toLowerCase();
        const buildCountry = this.buildCountryCode;
        if (buildCountry && envCountry && envCountry !== buildCountry) {
          this.L('loadConfiguration', 'stored env country mismatch, forcing refetch', {
            stored: envCountry,
            build: buildCountry
          });
          this._env = null;
          forced = true;
        }

        // Reject cached env when build target (prod vs UAT) does not match
        const desiredProduction = this.IsProduction();
        if (this._env && this._env.production !== desiredProduction) {
          this.L('loadConfiguration', 'build production flag mismatch, forcing refetch', {
            stored: this._env.production,
            desired: desiredProduction
          });
          this._env = null;
          forced = true;
        }

        // If forced and switching environments, check if production flag matches desired state
        if (forced && isUATToggle !== undefined && this._env) {
          const desiredProduction = !isUATToggle;
          if (this._env.production !== desiredProduction) {
            this.L('loadConfiguration', 'production flag mismatch, forcing refetch', {
              current: this._env.production,
              desired: desiredProduction
            });
            // Clear the cached env so we don't apply old URLs
            this._env = null;
            forced = true;
          }
        }

        if (this._env && this.allLocationsSet()) {
          // Only apply if not forcing a refetch (to avoid applying old URLs)
          if (!forced) {
            this.L('allLocationsSet', 'true');
            this.applyEnvIfChanged(); // non-blocking
          }
        } else {
          forced = true;
        }
      } catch {
        forced = true;
      }
    }

    // 2) Determine if we must refetch (TTL/version drift)
    const appInfo = await this.getAppInfo();
    let lastFetchTime = await SecureStorageService.get(SecureStorageKey.LastEnvironmentFetchTime);
    if (forced) lastFetchTime = null;

    if (this._env) {
      const versionChanged =
        !this._env.version ||
        !this._env.build ||
        this._env.version !== appInfo.version ||
        this._env.build !== appInfo.build;
      if (versionChanged) lastFetchTime = null;
    } else {
      lastFetchTime = null;
    }

    const expired = !lastFetchTime || Date.now() - parseInt(lastFetchTime, 10) > this.ENV_TTL_MS;

    // 3) Only resolve region if we need to refetch. Honor build-time countryCode when set (e.g. bomaid=za).
    if (expired) {
      const buildCountry = this.buildCountryCode;
      if (buildCountry) {
        this._selectedRegion = { countryCode: buildCountry, countryName: buildCountry.toUpperCase() };
        this.L('loadConfiguration', 'using build countryCode', this._selectedRegion);
      } else {
        const storedRegion = await SecureStorageService.get(SecureStorageKey.CurrentRegion);
        if (storedRegion) {
          try {
            this._selectedRegion = JSON.parse(storedRegion);
            this.L('loadConfiguration', 'storedRegion present=true', this._selectedRegion);
          } catch {
            this._selectedRegion = await this.countryFromIP();
          }
        } else {
          this.L('loadConfiguration', 'storedRegion present=false');
          this._selectedRegion = await this.countryFromIP();
        }
      }
    }

    // 4) If not expired, we’re done (env already applied)
    if (!expired) {
      this.L('loadConfiguration', 'fresh env in storage, skip fetch');
      return;
    }

    // 5) Fetch new env
    if (!(await this.isOnline())) return;

    await SecureStorageService.set(SecureStorageKey.LastEnvironmentFetchTime, String(Date.now()));

    const production = !isUATToggle;
    // Prefer build-time countryCode when set (e.g. bomaid) so API returns correct /health/za/ base URLs
    const effectiveCountry =
      this.buildCountryCode ??
      this._selectedRegion?.countryCode ??
      (this._env as { countryCode?: string })?.countryCode ??
      'gb';
    const req = {
      environmentName: environment.environmentName,
      countryCode: effectiveCountry,
      deviceType: Capacitor.getPlatform(),
      name: appInfo.name,
      appId: appInfo.id,
      version: appInfo.version,
      build: Number(appInfo.build),
      production,
    };

    try {
      const httpResponse = await firstValueFrom(
        appEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGet(this.http, this.appEnvCfg.rootUrl, req)
          .pipe(timeout(this.NETWORK_TIMEOUT_MS))
      );
      const result = httpResponse ? httpResponse.body : null;

      const local: ILocalAppEnvironment = {
        ...(result as IAppEnvironment),
        build: appInfo.build,
        deviceType: Capacitor.getPlatform(),
        id: appInfo.id,
        name: appInfo.name,
        version: appInfo.version,
        countryCode: req.countryCode,
        production,
        installSource: null,
      };

      await SecureStorageService.set(SecureStorageKey.LastEnvironmentFetchTime, String(Date.now()));
      await this.saveEnvironment(local);
    } catch (err) {
      await SecureStorageService.remove(SecureStorageKey.LastEnvironmentFetchTime);
      await this.prompt.showNetworkConnectionError(err, 'LoadConfiguration');
    }
  }

  private async saveEnvironment(config: ILocalAppEnvironment): Promise<void> {
    this._env = config;
    await SecureStorageService.set(StorageKey.EnvironmentSettings, JSON.stringify(config));
    this.applyEnvIfChanged();
  }

  private applyEnvIfChanged(): void {
    this.L('applyEnvIfChanged', 'BEGIN');
    if (!this._env) return;
    const snapshot = JSON.stringify(this._env);
    if (snapshot === this.lastAppliedEnvJson) return;

    this.lastAppliedEnvJson = snapshot;
    this.setupNetworkLocations(this._env);
    EventService.environmentLoaded.emit(this._env);
    this.L('applyEnvIfChanged', 'applied');
  }

  private setupNetworkLocations(env: ILocalAppEnvironment): void {
    this.L('setupNetworkLocations', 'BEGIN');
    // AppEnvironmentApiConfiguration gets its base URL from environment.ts, not from env config
    this.appEnvCfg.rootUrl = environment.appEnvironmentBaseUrl;
    this.vitalCfg.rootUrl = env.vitalScoreConfig!.vitalScoreBase!;
    this.faceCfg.rootUrl = env.faceScanConfig!.faceScanBase!;
    this.wearCfg.rootUrl = env.wearableConfig!.wearableV3Base!;
    this.authCfg.rootUrl = env.authenticateOptions!.authorizationServiceURL!;
    this.vhlCfg.rootUrl = env.virtualHealthLibraryConfig!.virtualHealthLibraryBase!;
    this.orgCfg.rootUrl = env.organizationConfig!.organizationBase!;
    this.reportCfg.rootUrl = env.reportingConfig!.reportingBase!;
    this.notifyCfg.rootUrl = env.notifyConfig!.notifyBase!;
    this.emotionCfg.rootUrl = env.emotionConfig!.emotionBase!;
    this.datapointsCfg.rootUrl = env.dataPointsConfig!.dataPointsBase!;
    this.pointsCfg.rootUrl = env.pointsConfig!.pointsBase!;
    this.questionnaireCfg.rootUrl = env.questionnaireConfig!.questionnaireBase!;
    this.mpiCfg.rootUrl = env.mpiConfig!.mpiBase!;
    // VivaLite uses sharedConfig instead of environment config
    this.vivaLiteScanCfg.rootUrl = sharedConfig.vivaLiteHealthCheckBase;
    const whatsAppBase =env.whatsAppConfig!.baseUrl!;
    this.whatsAppCfg.rootUrl = whatsAppBase;
    SystemLogService.enableLogging();
    this.L('setupNetworkLocations', 'END');
  }

  private allLocationsSet(): boolean {
    const e = this._env;
    if (!e) return false;
    return !!(
      e.vitalScoreConfig?.vitalScoreBase &&
      e.faceScanConfig?.faceScanBase &&
      e.wearableConfig?.wearableV3Base &&
      e.authenticateOptions?.authorizationServiceURL &&
      e.notifyConfig?.notifyBase &&
      e.virtualHealthLibraryConfig?.virtualHealthLibraryBase &&
      e.organizationConfig?.organizationBase &&
      e.mpiConfig?.mpiBase &&
      e.pointsConfig?.pointsBase &&
      e.reportingConfig?.reportingBase &&
      e.emotionConfig?.emotionBase &&
      e.dataPointsConfig?.dataPointsBase &&
      e.questionnaireConfig?.questionnaireBase &&
      e.whatsAppConfig?.baseUrl!
    );
  }

  private filteredCountries(list: CountryItemModel[] | null | undefined): CountryItemModel[] | null | undefined {
    if (!list) return list;
    const target = this.buildCountryCode;
    if (target) {
      return list.filter((c) => (c.countryCode ?? '').toLowerCase() === target);
    }
    return list;
  }

  private async isOnline(): Promise<boolean> {
    try {
      const status = await Network.getStatus();
      return !!status.connected;
    } catch {
      return false;
    }
  }

  private async getAppInfo(): Promise<AppInfo> {
    if (Capacitor.getPlatform() === 'web') {
      return {
        name: window.location.hostname,
        id: sharedConfig.webAppId!,
        version: sharedConfig.version,
        build: String(sharedConfig.build),
      };
    }
    return App.getInfo();
  }

  private async fetchWithTimeout(input: RequestInfo, init: RequestInit = {}, ms = 2000): Promise<Response> {
    const ctrl = new AbortController();
    const id = setTimeout(() => ctrl.abort(), ms);
    try {
      return await fetch(input, { ...init, signal: ctrl.signal });
    } finally {
      clearTimeout(id);
    }
  }

  /** Best-effort region detection; never blocks startup if it times out. */
  public async countryFromIP(): Promise<CountryItemModel | null> {
    this.L('countryFromIP', 'BEGIN');
    if (this._myCountry) return this._myCountry;
    if (!(await this.isOnline())) return this._myCountry;

    LoadingService.addSilentUrl('https://ipinfo.io');
    LoadingService.addSilentUrl(appEnvironmentMyCountryPost.PATH);

    try {
      this.L('countryFromIP', 'fetch ipinfo');
      const res = await this.fetchWithTimeout(
        `https://ipinfo.io/json?token=${sharedConfig.ipInfoToken}`,
        {},
        2000 // short local timeout
      );
      const ipInfo = await res.json();

      const body = {
        ip: ipInfo?.ip ?? '',
        city: ipInfo?.city ?? '',
        region: ipInfo?.region ?? '',
        country: ipInfo?.country ?? '',
        loc: ipInfo?.loc ?? '',
        org: ipInfo?.org ?? '',
        postal: ipInfo?.postal ?? '',
        timezone: ipInfo?.timezone ?? '',
      };

      this.L('countryFromIP', 'post MyCountry', body);

      const obs = appEnvironmentMyCountryPost(this.http, this.appEnvCfg.rootUrl, { body });
      const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT_MS)));
      this._myCountry = httpResponse ? httpResponse.body : null;

      this.L('countryFromIP', 'END', this._myCountry);
      return this._myCountry;
    } catch (err) {
      this.L('countryFromIP', 'ipinfo path ERROR', err);

      // Fallback: let server autodetect without IP payload
      try {
        const obs = appEnvironmentMyCountryPost(this.http, this.appEnvCfg.rootUrl, {
          body: { ip: '', city: '', region: '', country: '', loc: '', org: '', postal: '', timezone: '' },
        });
        const httpResponse2 = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT_MS)));
        this._myCountry = httpResponse2 ? httpResponse2.body : null;
        this.L('countryFromIP', 'fallback server-only success', this._myCountry);
        return this._myCountry;
      } catch {
        // Final fallback: GB (but DO NOT break init)
        this.L('countryFromIP', 'final fallback GB');
        return { countryCode: 'gb', countryName: 'United Kingdom' };
      }
    }
  }

  // ─────────────── debug logger ───────────────
  // small helper to standardize console output
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private L(scope: string, msg: string, ...rest: any[]): void {
    if (!this.LOG) return;
    // group logs under a consistent tag so they're easy to scan in the console
    // example output: "ENV][loadConfiguration] BEGIN { forced: false }"
    // Logging disabled - console.log removed
  }

  /** Build-time country code when set (e.g. bomaid=za). Avoids typing issues with environment.countryCode. */
  private get buildCountryCode(): string | undefined {
    const code = (environment as { countryCode?: string }).countryCode;
    return code != null ? String(code).toLowerCase() : undefined;
  }

  // ─────────────── misc state for methods above ───────────────
  private lastEmailTest = '';
  private userCountries: CountryItemModel[] | null | undefined = null;
}

// ─────────────── types ───────────────

export interface DevicePingResponse {
  networkSuccess: boolean;
  networkTime: number;
  deviceInfoSuccess: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pingError: any | null;
  pingResult: string | null;
}

export interface ILocalAppEnvironment extends IAppEnvironment {
  build?: string | null;
  deviceType?: string | null;
  id?: string | null;
  name?: string | null;
  version?: string | null;
  installSource?: string | null;
  production: boolean;
}